import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import {
  Megaphone,
  Download,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  FileText
} from 'lucide-react';

const PAGE_LIMIT = 10;

const PublicCirculars = () => {
  const [circulars, setCirculars] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [expandedDocs, setExpandedDocs] = useState(null);

  const fetchCirculars = (page = 1) => {
    setLoading(true);
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    axios
      .get(`${apiBase}/announcements/public?page=${page}&limit=${PAGE_LIMIT}`)
      .then((res) => {
        setCirculars(res.data.announcements || []);
        setPagination(res.data.pagination || { total: 0, page: 1, totalPages: 1 });
      })
      .catch(() => toast.error('Failed to load circulars'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCirculars(1);
  }, []);

  const handlePageChange = (newPage) => {
    setExpandedDocs(null);
    fetchCirculars(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDocDownload = (signedUrl) => {
    if (signedUrl) {
      window.open(signedUrl, '_blank');
    } else {
      toast.error('Download link unavailable, please refresh the page');
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });


  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <main className="flex-grow py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/" className="text-sm text-blue-600 hover:underline">
              ← Back to Home
            </Link>
            <div className="flex items-center gap-3 mt-3">
              <Megaphone className="h-7 w-7 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Public Circulars</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Official circulars and notices from Mon District Administration
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : circulars.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-lg border border-gray-200">
              <Megaphone className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No public circulars issued yet</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-5 py-3 text-left font-semibold text-gray-600 w-28">Date</th>
                      <th className="px-5 py-3 text-left font-semibold text-gray-600">Title &amp; Message</th>
                      <th className="px-5 py-3 text-left font-semibold text-gray-600 w-36">Attachments</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {circulars.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50 align-top">
                        <td className="px-5 py-4 text-gray-500 whitespace-nowrap">
                          {formatDate(item.created_at)}
                        </td>
                        <td className="px-5 py-4">
                          <p className="font-semibold text-gray-900 mb-1">{item.title}</p>
                          <p className="text-gray-500 text-xs whitespace-pre-wrap">{item.message}</p>
                          {item.author && (
                            <p className="text-xs text-gray-400 mt-1">by {item.author.fullName}</p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          {item.documents && item.documents.length > 0 ? (
                            <div>
                              <button
                                onClick={() =>
                                  setExpandedDocs(expandedDocs === item.id ? null : item.id)
                                }
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-xs"
                              >
                                <FileText className="h-3.5 w-3.5" />
                                {item.documents.length} file{item.documents.length > 1 ? 's' : ''}
                                {expandedDocs === item.id ? (
                                  <ChevronUp className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                )}
                              </button>
                              {expandedDocs === item.id && (
                                <div className="mt-1 space-y-1">
                                  {item.documents.map((doc, idx) => (
                                    <button
                                      key={idx}
                                      onClick={() => handleDocDownload(doc.signedUrl)}
                                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-blue-600 w-full text-left py-0.5"
                                    >
                                      <Download className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate max-w-[140px]">{doc.originalName}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-5">
                  <p className="text-sm text-gray-500">
                    Showing {((pagination.page - 1) * PAGE_LIMIT) + 1}–
                    {Math.min(pagination.page * PAGE_LIMIT, pagination.total)} of {pagination.total} circulars
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4" /> Previous
                    </button>
                    <span className="text-sm text-gray-600 px-2">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PublicCirculars;
